import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { MapContext } from '../../';
import { MapPin, Mail, Phone } from 'lucide-react';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { TbMessage } from 'react-icons/tb';
import RatingStars from './RatingStars';
import IconButton from './IconButton';
import VersatileLoader from '../../../../../components/VersatileLoader';
import ContactButtons from '../../../../../Services/ContactButtons';
import useSalesRepStats from './hooks/useSalesRepStats';
import useFiberCompanies from './hooks/useFiberCompanies';
import './MiniSalesRepCard.scss';
import { Target } from 'lucide-react';
import LiveTracking from "../../Images/map.png";
import profileImage from "./images/profile.png";


const MiniSalesRepCard = ({ salesRep, openNextRep, openPreviousRep, onTrack }) => {
    const map = useContext(MapContext);
    const { stats, loadingStats } = useSalesRepStats(salesRep?.id);
    const companies = useFiberCompanies(salesRep?.fiberCompanyId);
    const contactButtons = new ContactButtons();
  
    const formatDateToISO = (date) => {
      const pad = (n) => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
        date.getHours()
      )}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${String(date.getMilliseconds()).padStart(3, '0')}`;
    };
  
    const handleTrackRep = () => {
      if (!map || !salesRep?.id) return;
      const today = new Date();
      const startOfDay = new Date(today.setHours(0, 0, 0, 0));
      const endOfDay = new Date(today.setHours(23, 59, 59, 999));
      map.setSalesRepIds?.([salesRep.id]);
      map.setLocationFromDate?.(formatDateToISO(startOfDay));
      map.setLocationToDate?.(formatDateToISO(endOfDay));
      map.setTargetUserId?.(salesRep.id);
      map.setUserPathColor?.('#3f51b5');
      map.setCurrentSalesRepId(salesRep.id);
      if (onTrack) {
        onTrack(salesRep);
      }
    };
  
    return (
      <div className="mini-sales-rep-card">
        {loadingStats ? (
          <div className="loading-container">
            <VersatileLoader size="medium" color="#2761D0" />
            <p className="loading-text">Loading profile...</p>
          </div>
        ) : (
          <>
            <div className="card-header">
              <div className="profile-pic-wrapper">
                <img
                  src={salesRep?.profileImage?.url || profileImage }
                  className="profile-pic"
                  alt="Profile"
                  onError={(e) => (e.target.src = '')}
                  loading="lazy"
                />
                <div
                  className={`status-indicator ${salesRep?.isOnline ? 'online' : 'offline'}`}
                />
                <div className="rank-badge">
                  {/* #{stats.rank || 'NA'} */}
                  #{'NA'}
                </div>
              </div>
              <div className="profile-info">
                <h3 className="name">
                  {salesRep?.firstName} {salesRep?.lastName}
                </h3>
                <RatingStars rating={stats.avgRating} totalReviews={stats.totalReviews} />
                <div className="location">
                  <MapPin className="icon" />
                  <span>{salesRep?.address || 'Location not available'}</span>
                </div>
              </div>
            </div>
  
            <div className="card-body">
              {companies.length > 0 && (
                <div className="companies-section">
                  <h4 className="section-title">Companies</h4>
                  <div className="companies-list">
                    {companies.map((company, i) => (
                      <div key={i} className="company-item">
                        <img
                          src={`data:image/png;base64,${company.logo}`}
                          className="company-logo"
                          alt={company.name}
                          loading="lazy"
                        />
                        <span className="company-name">{company.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="performance-section">
                <h4 className="section-title">Performance</h4>
                <div className="stats-grid">
                  {[
                    { value: stats.totalSales, label: 'Sales', color: 'blue' },
                    { value: stats.totalInstalls, label: 'Installs', color: 'green' },
                    { value: 0, label: 'Referrals', color: 'purple' },
                    { value: stats.totalReviews, label: 'Reviews', color: 'orange' },
                  ].map((stat, i) => (
                    <div key={i} className="stat-box">
                      <div className={`stat-value ${stat.color}`}>{stat.value.toLocaleString()}</div>
                      <div className="stat-label">{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
  
            <div className="card-footer">
              <div className="footer-left">
                <IconButton onClick={openPreviousRep} icon={FaArrowLeft} tooltip="Previous rep" />
                <IconButton onClick={() => contactButtons.sms(salesRep.phoneNumber)} icon={TbMessage} tooltip="Send message" />
              </div>
  
              <button 
  className={`track-button ${map.targetUserId === salesRep?.id ? 'active' : ''}`}
  onClick={handleTrackRep}
>
  <div className="button-content">
    {/* <Target className="track-icon" size={20} /> */}
    <img
                src={LiveTracking}
                alt="Live Tracking"
                style={{
                  width: "18px",
                  height: "18px",
                  maxWidth: "none",
                  zIndex: "3",
                  position: "relative",
                }}
              />
    <span>
      Track
    </span>
  </div>
</button>
  
              <div className="footer-right">
                <IconButton onClick={() => contactButtons.email(salesRep.email)} icon={Mail} tooltip="Send email" />
                <IconButton onClick={() => contactButtons.call(salesRep.phoneNumber)} icon={Phone} tooltip="Make call" />
                <IconButton onClick={openNextRep} icon={FaArrowRight} tooltip="Next rep" />
              </div>
            </div>
          </>
        )}
      </div>
    );
  };
  
  MiniSalesRepCard.propTypes = {
    salesRep: PropTypes.shape({
      id: PropTypes.number,
      fiberCompanyId: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      profileImage: PropTypes.shape({ url: PropTypes.string }),
      address: PropTypes.string,
      phoneNumber: PropTypes.string,
      email: PropTypes.string,
      isOnline: PropTypes.bool,
    }).isRequired,
    openNextRep: PropTypes.func.isRequired,
    openPreviousRep: PropTypes.func.isRequired,
    onTrack: PropTypes.func,
  };
  
  export default MiniSalesRepCard;
